import React, { useContext, useEffect, useState } from 'react';
import {
	classNames,
	createDataTree,
	getUserFullName,
} from '../helpers/functions';
import useModal from '../../hooks/useModal';
import useAsync from '../../hooks/useAsync';
import { getLocations } from '../../api';
import {
	ChevronDownIcon,
	PlusIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import ReactSelect, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import Button from '../Button';
import {
	getModuleCategories,
	getModuleCategoriesByModuleKey,
	getModules,
} from '../../api/modules';
import { getContacts } from '../../api/contacts';
import SettingsContext from '../../context/SettingsContext';
import usePrevious from '../../hooks/usePrevious';
import DatePicker from 'react-datepicker';
import { parseISO } from 'date-fns';
import { getTeam } from '../../api/company';

const DropdownIndicator = (props) => {
	return (
		<components.DropdownIndicator {...props}>
			<ChevronDownIcon className="w-4 h-4 text-gray-500" />
		</components.DropdownIndicator>
	);
};

const ClearIndicator = (props) => {
	return (
		<components.ClearIndicator {...props}>
			<XMarkIcon className="w-8 h-8 text-gray-400 px-2 py-1 cursor-pointer -mr-2" />
		</components.ClearIndicator>
	);
};

const CustomMenuList = ({ customAction, ...props }) => {
	return (
		<components.MenuList {...props}>
			{props.children}
			{customAction ? (
				<>
					<div className="border-t border-gray-200">
						<Button
							type="ghost"
							Icon={PlusIcon}
							onClick={() => customAction?.onClick()}
						>
							{customAction?.label}{' '}
						</Button>
					</div>
				</>
			) : null}
		</components.MenuList>
	);
};

const getValue = (field, obj) => {
	return (
		obj[field?.key] ||
		obj?.custom_attributes?.find((o) => o.attribute_field_id === field?.id)
			?.value
	);
};

// This is used to find a nested object by ID and return the parent objects
function findPathById(items, targetId, path = []) {
	for (const item of items) {
		const newPath = [...path, item];
		if (item.id === targetId) {
			return newPath;
		}
		if (item.children) {
			const result = findPathById(item.children, targetId, newPath);
			if (result) return result;
		}
	}
	return null;
}

const handleAttributeReduce = (moduleFields, skip) =>
	moduleFields?.combined
		?.filter((field) => !skip?.includes(field?.key))
		?.reduce(
			(prev, cur) => {
				prev[cur?.isDefault ? 'default' : 'custom'].push(cur);
				return prev;
			},
			{ default: [], custom: [] },
		);

const DetailList = ({
	entity,
	moduleFields,
	skipFields = [],
	onUpdate,
	onRefresh,
	moduleId,
	show = 'custom',
	alwaysExtended,
	skipContainer,
}) => {
	const {
		settings,
		loading: isLoadingSettings,
		update: updateSettings,
	} = useContext(SettingsContext);

	const [selectedAttributes, setSelectedAttributes] = useState({});

	const [extended, setExtended] = useState(
		alwaysExtended || settings?.user[`extendedAttributes${moduleId}`],
	);

	const { value: locations, status: isLoadingLocations } =
		useAsync(getLocations);

	const { setOpen: openNewCategory } = useModal('addCategoryModal');
	const { setOpen: openNewVendor } = useModal('addVendorModal');

	const { value: vendors, status: isLoadingVendors } = useAsync(getContacts);
	const { value: team, status: isLoadingTeam } = useAsync(getTeam);

	const {
		value: moduleCategories,
		status: isLoadingCategories,
		execute: onLoadModuleCategories,
	} = useAsync(getModuleCategories, {
		immediate: false,
	});

	const { execute: onLoadModuleCategoriesByKey } = useAsync(
		getModuleCategoriesByModuleKey,
		{
			immediate: false,
		},
	);

	const prevExtendedSettings = usePrevious(
		settings?.user[`extendedAttributes${moduleId}`],
	);
	useEffect(() => {
		if (!alwaysExtended) {
			if (
				!prevExtendedSettings &&
				settings?.user[`extendedAttributes${moduleId}`]
			) {
				setExtended(settings?.user[`extendedAttributes${moduleId}`]);
			} else {
				setExtended(false);
			}
		}
	}, [settings?.user[`extendedAttributes${moduleId}`]]);

	const { value: modules } = useAsync(getModules);

	const { open: selectLocationOptions, setOpen: openSelectLocation } =
		useModal('selectLocation');

	const { open: manageAttributesSettings, setOpen: openManageAttributes } =
		useModal('manageAttributes');

	const contractCategoryOptions = moduleCategories?.map((category) => ({
		label: category?.name,
		value: category?.id,
	}));

	const vendorOptions = vendors?.rows?.map((vendor) => ({
		label: vendor?.companyName || getUserFullName(vendor),
		value: vendor?.id,
	}));

	const teamOptions = team?.rows?.map((member) => ({
		label: getUserFullName(member),
		value: member?.id,
	}));

	const booleanOptions = [
		{ label: 'True', value: 'true' },
		{ label: 'False', value: 'false' },
	];

	const locationOptionsTree = createDataTree(locations?.rows, 'children');

	const reducedAttributes =
		handleAttributeReduce(moduleFields, skipFields) || {};

	const CONTAINER = skipContainer ? React.Fragment : DetailListContainer;

	return (
		<>
			<CONTAINER>
				{reducedAttributes[show] && reducedAttributes[show]?.length && extended
					? reducedAttributes[show]
							?.sort((a, b) => {
								if (a.order !== b.order) {
									return a.order - b.order;
								}
								return a.label
									.toLowerCase()
									.localeCompare(b.label.toLowerCase());
							})
							?.map((field) => {
								const value = getValue(field, entity);
								return (
									<DetailListItem label={field?.label}>
										{(() => {
											switch (field?.type) {
												case 'text':
													return (
														<input
															placeholder="-"
															defaultValue={value}
															className={classNames(
																'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
															)}
															onBlur={(e) => {
																onUpdate(
																	{
																		id: entity?.id,
																		customAttributes: [
																			{
																				fieldId: field?.id,
																				value: e.target.value,
																			},
																		],
																	},
																	() => {
																		onRefresh({ id: entity?.id }, null, {
																			silent: true,
																		});
																	},
																);
															}}
														/>
													);
												case 'textarea':
													return (
														<textarea
															className={classNames(
																'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
															)}
															placeholder="-"
															height="auto"
															rows={3}
															defaultValue={value}
															onBlur={(e) => {
																onUpdate(
																	{
																		id: entity?.id,
																		...(field?.isDefault
																			? {
																					[field?.key]: e.target.value,
																				}
																			: {
																					customAttributes: [
																						{
																							fieldId: field?.id,
																							value: e.target.value,
																						},
																					],
																				}),
																	},
																	() => {
																		onRefresh({ id: entity?.id }, null, {
																			silent: true,
																		});
																	},
																);
															}}
														/>
													);
												case 'numeric':
													return (
														<input
															placeholder="-"
															defaultValue={value}
															className={classNames(
																'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
															)}
															onBlur={(e) => {
																onUpdate(
																	{
																		id: entity?.id,
																		customAttributes: [
																			{
																				fieldId: field?.id,
																				value: e.target.value,
																			},
																		],
																	},
																	() => {
																		onRefresh({ id: entity?.id }, null, {
																			silent: true,
																		});
																	},
																);
															}}
															type="number"
															pattern="^-?[0-9]\d*\.?\d*$"
														/>
													);
												case 'location':
													return (
														<div className="relative w-full">
															<div
																className={classNames(
																	'w-full pr-10 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
																)}
																onClick={() => {
																	openSelectLocation({
																		locations,
																		onSelect: (selected) => {
																			onUpdate(
																				{
																					id: entity?.id,
																					customAttributes: [
																						{
																							fieldId: field?.id,
																							value: selected?.id,
																						},
																					],
																				},
																				(payload, err) => {
																					onRefresh(
																						{
																							id: entity?.id,
																						},
																						null,
																						{
																							silent: true,
																						},
																					);
																				},
																			);
																		},
																		selected: value,
																	});
																}}
															>
																{findPathById(locationOptionsTree, value)
																	?.map((o) => o.name)
																	?.reverse()
																	.map((name, index) => (
																		<span
																			className={
																				index === 0
																					? 'text-black'
																					: 'text-gray-400 ml-3'
																			}
																		>
																			{name}
																		</span>
																	)) || '-'}
															</div>
															{value && value !== '-' && (
																<XMarkIcon
																	className="absolute right-3 top-0 mt-0 w-8 h-8 text-gray-500 px-2 py-1 cursor-pointer"
																	onClick={() => {
																		onUpdate(
																			{
																				id: entity?.id,
																				customAttributes: [
																					{
																						fieldId: field?.id,
																						value: null,
																					},
																				],
																			},
																			(payload, err) => {
																				onRefresh(
																					{
																						id: entity?.id,
																					},
																					null,
																					{
																						silent: true,
																					},
																				);
																			},
																		);
																	}}
																/>
															)}
														</div>
													);
												case 'category': {
													const assumedCategoryModuleId = modules?.rows?.find(
														(o) =>
															field?.options?.module_key
																? o?.key === field?.options?.module_key
																: o?.id === (field?.moduleId || moduleId),
													)?.id;

													return (
														<AsyncSelect
															unstyled
															backspaceRemovesValue={false}
															styles={{
																control: (provided) => {
																	return {
																		...provided,
																		outline: '',
																		cursor: 'pointer',
																	};
																},
																option: (provided, state) => {
																	return {
																		...provided,
																		paddingLeft: `${
																			(state.data.depth || 0) * 16 + 10
																		}px`,
																	};
																},
															}}
															classNames={{
																control: ({ isFocused }) =>
																	classNames(
																		isFocused &&
																			'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																		'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																	),
																input: ({ isFocused }) => '',
																valueContainer: () => '',
																placeholder: () => 'py-2.5',
																menu: () =>
																	'shadow-md border border-gray-100 bg-white',
																option: ({ isFocused, isSelected, isGroup }) =>
																	classNames(
																		isFocused &&
																			'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																		isSelected &&
																			'hover:border-indigo-500 !border-indigo-500 ',
																		'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																	),
																noOptionsMessage: () => 'py-4',
															}}
															placeholder="-"
															noOptionsMessage={() => 'No category found'}
															components={{
																ClearIndicator,

																...(value
																	? {
																			DropdownIndicator: () => null,
																		}
																	: { DropdownIndicator }),
																MenuList: (props) => (
																	<CustomMenuList
																		{...props}
																		customAction={{
																			label: 'Add a new category',
																			onClick: () =>
																				openNewCategory({
																					moduleId:
																						assumedCategoryModuleId ||
																						field?.moduleId ||
																						moduleId,
																					onSubmit: (newCategory) =>
																						onUpdate(
																							{
																								id: entity?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value: newCategory?.value,
																									},
																								],
																							},
																							(payload) => {
																								onRefresh(
																									{
																										id: entity?.id,
																									},
																									null,
																									{
																										silent: true,
																									},
																								);
																							},
																						),
																				}),
																		}}
																	/>
																),
															}}
															value={(
																selectedAttributes[field?.id] || []
															)?.find((o) => o.value === value)}
															isDisabled={isLoadingCategories === 'pending'}
															onChange={(newValue) => {
																onUpdate(
																	{
																		id: entity?.id,
																		...(field?.isDefault
																			? {
																					[field?.key]: newValue?.value || null,
																				}
																			: {
																					customAttributes: [
																						{
																							fieldId: field?.id,
																							value: newValue?.value || null,
																						},
																					],
																				}),
																	},
																	(payload) => {
																		onRefresh({ id: entity?.id }, null, {
																			silent: true,
																		});
																	},
																);
															}}
															defaultOptions
															cacheOptions
															isClearable
															isSearchable={false}
															loadOptions={async (input) => {
																if (!input) {
																	const onLoadCategories = field?.options
																		?.module_key
																		? onLoadModuleCategoriesByKey
																		: onLoadModuleCategories;

																	const options = await onLoadCategories({
																		key: field?.options?.module_key,
																		id: field?.moduleId || moduleId,
																	});

																	const loadedOptions = options?.map((o) => ({
																		label: o.name,
																		value: o.id,
																	}));

																	setSelectedAttributes({
																		...selectedAttributes,
																		[field?.id]: loadedOptions,
																	});

																	return loadedOptions;
																}
															}}
															isLoading={isLoadingCategories === 'pending'}
															loadingMessage={() => 'Loading categories...'}
														/>
													);
												}
												case 'boolean':
													return (
														<ReactSelect
															unstyled
															backspaceRemovesValue={false}
															styles={{
																control: (provided) => {
																	return {
																		...provided,
																		outline: '',
																		cursor: 'pointer',
																	};
																},
																option: (provided, state) => {
																	return {
																		...provided,
																		paddingLeft: `${
																			(state.data.depth || 0) * 16 + 10
																		}px`,
																	};
																},
															}}
															classNames={{
																control: ({ isFocused }) =>
																	classNames(
																		isFocused &&
																			'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																		'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																	),
																input: ({ isFocused }) => '',
																valueContainer: () => '',
																placeholder: () => 'py-2.5',
																menu: () =>
																	'shadow-md border border-gray-100 bg-white',
																option: ({ isFocused, isSelected, isGroup }) =>
																	classNames(
																		isFocused &&
																			'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																		isSelected &&
																			'hover:border-indigo-500 !border-indigo-500 ',
																		'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																	),
																noOptionsMessage: () => 'py-4',
															}}
															placeholder="-"
															isSearchable={false}
															components={{
																ClearIndicator,

																...(value
																	? {
																			DropdownIndicator: () => null,
																		}
																	: { DropdownIndicator }),
															}}
															value={booleanOptions?.find(
																(o) => o.value === value,
															)}
															options={booleanOptions}
															onChange={(newValue) => {
																onUpdate(
																	{
																		id: entity?.id,
																		customAttributes: [
																			{
																				fieldId: field?.id,
																				value: newValue?.value || null,
																			},
																		],
																	},
																	() => {
																		onRefresh({ id: entity?.id }, null, {
																			silent: true,
																		});
																	},
																);
															}}
															isClearable
														/>
													);
												case 'options':
													const options = field?.options?.options || [];
													return (
														<ReactSelect
															unstyled
															backspaceRemovesValue={false}
															styles={{
																control: (provided) => {
																	return {
																		...provided,
																		outline: '',
																		cursor: 'pointer',
																	};
																},
																option: (provided, state) => {
																	return {
																		...provided,
																		paddingLeft: `${
																			(state.data.depth || 0) * 16 + 10
																		}px`,
																	};
																},
															}}
															classNames={{
																control: ({ isFocused }) =>
																	classNames(
																		isFocused &&
																			'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																		'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																	),
																input: ({ isFocused }) => '',
																valueContainer: () => '',
																placeholder: () => 'py-2.5',
																menu: () =>
																	'shadow-md border border-gray-100 bg-white',
																option: ({ isFocused, isSelected, isGroup }) =>
																	classNames(
																		isFocused &&
																			'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																		isSelected &&
																			'hover:border-indigo-500 !border-indigo-500 ',
																		'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																	),
																noOptionsMessage: () => 'py-4',
															}}
															placeholder="-"
															isSearchable={false}
															components={{
																ClearIndicator,

																...(value
																	? {
																			DropdownIndicator: () => null,
																		}
																	: { DropdownIndicator }),
															}}
															value={options?.find((o) => o.value === value)}
															options={options}
															onChange={(newValue) => {
																onUpdate(
																	{
																		id: entity?.id,
																		...(field?.isDefault
																			? {
																					[field?.key]: newValue?.value || null,
																				}
																			: {
																					customAttributes: [
																						{
																							fieldId: field?.id,
																							value: newValue?.value || null,
																						},
																					],
																				}),
																	},
																	() => {
																		onRefresh({ id: entity?.id }, null, {
																			silent: true,
																		});
																	},
																);
															}}
															isClearable
														/>
													);

												case 'team':
													return (
														<ReactSelect
															unstyled
															backspaceRemovesValue={false}
															styles={{
																control: (provided) => {
																	return {
																		...provided,
																		outline: '',
																		cursor: 'pointer',
																	};
																},
																option: (provided, state) => {
																	return {
																		...provided,
																		paddingLeft: `${
																			(state.data.depth || 0) * 16 + 10
																		}px`,
																	};
																},
															}}
															classNames={{
																control: ({ isFocused }) =>
																	classNames(
																		isFocused &&
																			'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																		'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																	),
																input: ({ isFocused }) => '',
																valueContainer: () => '',
																placeholder: () => 'py-2.5',
																menu: () =>
																	'shadow-md border border-gray-100 bg-white',
																option: ({ isFocused, isSelected, isGroup }) =>
																	classNames(
																		isFocused &&
																			'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																		isSelected &&
																			'hover:border-indigo-500 !border-indigo-500 ',
																		'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																	),
																noOptionsMessage: () => 'py-4',
															}}
															placeholder="-"
															noOptionsMessage={() => 'Not found'}
															components={{
																ClearIndicator,

																...(value
																	? {
																			DropdownIndicator: () => null,
																		}
																	: { DropdownIndicator }),
															}}
															value={teamOptions?.find(
																(o) => o.value === value,
															)}
															isDisabled={isLoadingTeam === 'pending'}
															options={teamOptions}
															onChange={(newValue) => {
																onUpdate(
																	{
																		id: entity?.id,
																		assignedTo: newValue?.value || null,
																	},
																	(payload) => {
																		onRefresh({ id: entity?.id }, null, {
																			silent: true,
																		});
																	},
																);
															}}
															isClearable
														/>
													);
												case 'date':
													return (
														<DetailListDate
															value={value}
															field={field}
															onRefresh={onRefresh}
															onUpdate={onUpdate}
															entity={entity}
														/>
													);
												case 'vendor':
													return (
														<ReactSelect
															unstyled
															backspaceRemovesValue={false}
															styles={{
																control: (provided) => {
																	return {
																		...provided,
																		outline: '',
																		cursor: 'pointer',
																	};
																},
																option: (provided, state) => {
																	return {
																		...provided,
																		paddingLeft: `${
																			(state.data.depth || 0) * 16 + 10
																		}px`,
																	};
																},
															}}
															classNames={{
																control: ({ isFocused }) =>
																	classNames(
																		isFocused &&
																			'!outline-none !-outline-offset-2 !outline-indigo-500 ',
																		'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
																	),
																input: ({ isFocused }) => '',
																valueContainer: () => '',
																placeholder: () => 'py-2.5',
																menu: () =>
																	'shadow-md border border-gray-100 bg-white',
																option: ({ isFocused, isSelected, isGroup }) =>
																	classNames(
																		isFocused &&
																			'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
																		isSelected &&
																			'hover:border-indigo-500 !border-indigo-500 ',
																		'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
																	),
																noOptionsMessage: () => 'py-4',
															}}
															placeholder="-"
															noOptionsMessage={() => 'Not found'}
															components={{
																ClearIndicator,

																...(value
																	? {
																			DropdownIndicator: () => null,
																		}
																	: { DropdownIndicator }),
																MenuList: (props) => (
																	<CustomMenuList
																		{...props}
																		customAction={{
																			label: 'Add a company',
																			onClick: () =>
																				openNewVendor({
																					onSubmit: (vendor) => {
																						onUpdate(
																							{
																								id: entity?.id,
																								customAttributes: [
																									{
																										fieldId: field?.id,
																										value: vendor?.value,
																									},
																								],
																							},
																							(payload) => {
																								onRefresh(
																									{
																										id: entity?.id,
																									},
																									null,
																									{
																										silent: true,
																									},
																								);
																							},
																						);
																					},
																				}),
																		}}
																	/>
																),
															}}
															value={vendorOptions?.find(
																(o) => o.value === value,
															)}
															isDisabled={isLoadingVendors === 'pending'}
															options={vendorOptions}
															onChange={(newValue) => {
																onUpdate(
																	{
																		id: entity?.id,
																		customAttributes: [
																			{
																				fieldId: field?.id,
																				value: newValue?.value || null,
																			},
																		],
																	},
																	(payload) => {
																		onRefresh({ id: entity?.id }, null, {
																			silent: true,
																		});
																	},
																);
															}}
															isClearable
														/>
													);
											}
										})()}
									</DetailListItem>
								);
							})
					: null}
			</CONTAINER>
			{show !== 'default' && (
				<div className="pt-3">
					{reducedAttributes[show]?.length && !alwaysExtended ? (
						<>
							<a
								className="text-indigo-800 hover:text-indigo-600 text-xs cursor-pointer mr-1.5"
								onClick={() => {
									updateSettings({
										type: 'user',
										key: `extendedAttributes${moduleId}`,
										value: !extended,
									});
									setExtended(!extended);
								}}
							>
								{`${extended ? 'Hide' : 'Show'} (${
									reducedAttributes[show]?.length
								}) fields`}
							</a>
							<span className="text-gray-300 mr-1.5">-</span>
						</>
					) : null}

					<a
						className="text-indigo-800 hover:text-indigo-600 text-xs cursor-pointer"
						onClick={() => {
							openManageAttributes({
								moduleId,
								onSelect: (payload) => {},
							});
						}}
					>
						Manage fields
					</a>
				</div>
			)}
		</>
	);
};

export const DetailListContainer = ({ children }) => (
	<ul role="list" className="divide-y divide-gray-100 text-xs">
		{children}
	</ul>
);

export const DetailListItem = ({ label, children, labelClassName }) => (
	<li className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4  sm:flex-nowrap text-xs">
		<div className="grid grid-cols-2 gap-x-6 flex-1">
			<div className={classNames('text-gray-500 pt-2', labelClassName)}>
				{label}
			</div>
			<div className="">
				<div>{children}</div>
			</div>
		</div>
	</li>
);

export const DetailListOptions = ({
	value,
	field,
	onUpdate,
	entity,
	onRefresh,
	options = [],
	isClearable,
	onChange,
	newOptionConfig = {},
}) => {
	return (
		<ReactSelect
			unstyled
			backspaceRemovesValue={false}
			styles={{
				control: (provided) => {
					return {
						...provided,
						outline: '',
						cursor: 'pointer',
					};
				},
				option: (provided, state) => {
					return {
						...provided,
						paddingLeft: `${(state.data.depth || 0) * 16 + 10}px`,
					};
				},
			}}
			classNames={{
				control: ({ isFocused }) =>
					classNames(
						isFocused &&
							'!outline-none !-outline-offset-2 !outline-indigo-500 ',
						'px-3 outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
					),
				input: ({ isFocused }) => '',
				valueContainer: () => '',
				placeholder: () => 'py-2.5',
				menu: () => 'shadow-md border border-gray-100 bg-white',
				option: ({ isFocused, isSelected, isGroup }) =>
					classNames(
						isFocused &&
							'bg-gray-100 active:bg-gray-200 border-l-4 !border-indigo-500',
						isSelected && 'hover:border-indigo-500 !border-indigo-500 ',
						'hover:cursor-pointer px-3 py-2 border-l-4 border-white active:border-gray-100 hover:border-l-4 hover:border-indigo-500',
					),
				noOptionsMessage: () => 'py-4',
			}}
			placeholder="-"
			isSearchable={false}
			components={{
				ClearIndicator,

				...(value && isClearable
					? {
							DropdownIndicator: () => null,
						}
					: { DropdownIndicator }),

				...(newOptionConfig?.label
					? {
							MenuList: (props) => (
								<CustomMenuList
									{...props}
									customAction={{
										label: newOptionConfig?.label,
										onClick: () => newOptionConfig?.onClick(),
									}}
								/>
							),
						}
					: {}),
			}}
			value={options?.find((o) => o.value === value)}
			options={options}
			onChange={(newValue) => {
				if (typeof onUpdate === 'function') {
					onUpdate(
						{
							id: entity?.id,
							...(field?.isDefault
								? {
										[field?.key]: newValue?.value || null,
									}
								: {
										customAttributes: [
											{
												fieldId: field?.id,
												value: newValue?.value || null,
											},
										],
									}),
						},
						() => {
							onRefresh({ id: entity?.id }, null, {
								silent: true,
							});
						},
					);
				}

				if (typeof onChange === 'function') {
					onChange(newValue);
				}
			}}
			isClearable={isClearable}
		/>
	);
};

export const DetailListDate = ({
	value,
	field,
	onUpdate,
	entity,
	onRefresh,
	onChange,
	type, // null | error
}) => {
	const formatDate = (date) => {
		if (!date) {
			return null;
		}
		if (typeof date === 'string') {
			return parseISO(value);
		}
		if (typeof date === 'object') {
			return value;
		}
	};

	return (
		<div className="relative w-full">
			<DatePicker
				className={classNames(
					'placeholder:text-xs block w-full placeholder:text-gray-900 appearance-none px-3 py-2.5 sm:text-xs outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500',
					type === 'error' ? 'text-red-700 bg-red-50' : '',
				)}
				wrapperClassName="block w-full"
				placeholderText={'-'}
				selected={formatDate(value)}
				onChange={(date) => {
					if (typeof onUpdate === 'function') {
						onUpdate(
							{
								id: entity?.id,
								customAttributes: [
									{
										fieldId: field?.id,
										value: date || null,
									},
								],
							},
							(payload) => {
								onRefresh({ id: entity?.id }, null, {
									silent: true,
								});
							},
						);
					} else if (typeof onChange === 'function') {
						onChange(date);
					}
				}}
				dateFormat="MM/dd/yyyy"
			/>

			{value && (
				<XMarkIcon
					className="absolute right-1 top-0 mt-0 w-8 h-8 text-gray-400 px-2 py-1 cursor-pointer"
					onClick={() => {
						if (typeof onUpdate === 'function') {
							onUpdate(
								{
									id: entity?.id,
									customAttributes: [
										{
											fieldId: field?.id,
											value: null,
										},
									],
								},
								(payload, err) => {
									onRefresh(
										{
											id: entity?.id,
										},
										null,
										{
											silent: true,
										},
									);
								},
							);
						} else if (typeof onChange === 'function') {
							onChange(null);
						}
					}}
				/>
			)}
		</div>
	);
};

export const DetailListTextarea = ({
	value,
	field,
	onUpdate,
	entity,
	onRefresh,
}) => {
	return (
		<textarea
			className={classNames(
				'placeholder:text-black outline-1 focus:outline-none focus:-outline-offset-2 hover:-outline-offset-1 outline-none -outline-offset-2 hover:outline-gray-400 focus:outline-indigo-500 py-2.5 px-3 h-full w-full',
			)}
			placeholder="-"
			height="auto"
			rows={3}
			defaultValue={value}
			onBlur={(e) => {
				onUpdate(
					{
						id: entity?.id,
						...(field?.isDefault
							? {
									[field?.key]: e.target.value,
								}
							: {
									customAttributes: [
										{
											fieldId: field?.id,
											value: e.target.value,
										},
									],
								}),
					},
					() => {
						onRefresh({ id: entity?.id }, null, {
							silent: true,
						});
					},
				);
			}}
		/>
	);
};

export default DetailList;
